/**
 * Publishes the height of the sticky bar currently on screen as
 * `--chat-sticky-clearance`, which chat-clearance.css uses to lift the chat button
 * clear of it. Zero when no bar is showing, so the button sits where the app put it.
 *
 * Shared by the four bars that pin themselves to the bottom of the viewport:
 * biosphere-sticky-atc (product pages), product-sticky-bar and lp-sticky-bar
 * (landing pages) and biosphere-combo-buy's own bar.
 *
 * They do not agree on how they hide: three use the `hidden` attribute, lp-sticky-bar
 * stays in the layout and slides out on a transform. So presence is judged by where
 * the bar actually is — an IntersectionObserver against the viewport catches both,
 * and catches the slide finishing rather than measuring mid-animation.
 */

const BARS =
  "biosphere-sticky-atc, .pro_sticky_bar, .biosphere-combo-buy__sticky, .lpstick";
const PROPERTY = "--chat-sticky-clearance";

/* The gap is CSS's to decide — chat-clearance.css sets it per breakpoint, since the
   button already carries an inset of its own that reads as part of the gap. */
const GAP = "var(--chat-sticky-gap)";

const onScreen = new Set();
let frame = 0;

function measure() {
  frame = 0;

  let tallest = 0;
  for (const bar of onScreen) {
    if (bar.hasAttribute("hidden") || !bar.isConnected) continue;
    tallest = Math.max(tallest, bar.getBoundingClientRect().height);
  }

  const root = document.documentElement;
  if (tallest > 0) {
    root.style.setProperty(PROPERTY, `calc(${Math.round(tallest)}px + ${GAP})`);
  } else {
    root.style.removeProperty(PROPERTY);
  }
}

/* The bars toggle on scroll, so measuring is batched to a frame. */
function update() {
  if (frame) return;
  frame = requestAnimationFrame(measure);
}

const bars = document.querySelectorAll(BARS);
if (bars.length) {
  const visibility = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) onScreen.add(entry.target);
        else onScreen.delete(entry.target);
      }
      update();
    },
    { threshold: 0 },
  );

  /* Height changes when the variant, tier or schedule line changes, and when the
     viewport does; `hidden` changes without the bar ever moving. */
  const attributes = new MutationObserver(update);
  const size = new ResizeObserver(update);

  for (const bar of bars) {
    visibility.observe(bar);
    attributes.observe(bar, { attributes: true, attributeFilter: ["hidden"] });
    size.observe(bar);
  }

  /* The slide-in ends below the fold as far as the observer is concerned only once
     the transform lands, so re-measure when it does. */
  document.addEventListener("transitionend", (event) => {
    if (event.propertyName === "transform" && event.target.matches(BARS)) {
      update();
    }
  });

  update();
}
